import React, { useEffect, useMemo, useState, type JSX } from "react";
import { ShieldAlert } from "lucide-react";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import type { AdminQueryError } from "../../contracts/admin-query-errors";
import type { AdminRecognitionRawAccessRequest } from "../../contracts/admin-read-models";
import type { RecognitionAuditRawActionViewModel } from "./recognition-audit-view-model";

type RecognitionAuditRawAccessDialogProps = {
  action?: RecognitionAuditRawActionViewModel;
  error?: AdminQueryError;
  locale: AdminDashboardLocale;
  onClose: () => void;
  onSubmit: (input: AdminRecognitionRawAccessRequest) => Promise<void>;
  recognitionRunId: string;
  submitting: boolean;
};

function panelStyle() {
  return {
    padding: "18px",
    borderRadius: "18px",
    border: "1px solid rgba(148, 163, 184, 0.24)",
    background: "rgba(255, 255, 255, 0.95)",
    display: "grid",
    gap: "14px",
  } as const;
}

function controlStyle() {
  return {
    width: "100%",
    minHeight: "40px",
    padding: "10px 12px",
    borderRadius: "12px",
    border: "1px solid rgba(148, 163, 184, 0.45)",
    background: "#fff",
    color: "#18212b",
    fontSize: "0.92rem",
  } as const;
}

export function RecognitionAuditRawAccessDialog(
  props: RecognitionAuditRawAccessDialogProps,
): JSX.Element | null {
  const actionTargetKind = props.action?.targetKind ?? "source_file";
  const actionTargetId = props.action?.targetId ?? "";
  const [targetKind, setTargetKind] = useState<AdminRecognitionRawAccessRequest["targetKind"]>(
    actionTargetKind,
  );
  const [targetId, setTargetId] = useState(actionTargetId);
  const [purpose, setPurpose] = useState<AdminRecognitionRawAccessRequest["purpose"]>(
    "failure_analysis",
  );
  const [reason, setReason] = useState("");
  const [linkedIssueId, setLinkedIssueId] = useState("");
  const [validationMessage, setValidationMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    setTargetKind(actionTargetKind);
    setTargetId(actionTargetId);
  }, [actionTargetKind, actionTargetId]);

  const copy = useMemo(
    () => ({
      title: pickAdminDashboardLocaleText(props.locale, { en: "Raw access request", ko: "Raw 접근 요청" }),
      guidance: pickAdminDashboardLocaleText(props.locale, {
        en: "Every raw access leaves an audit trail. Use the smallest scope and keep the retention reason specific.",
        ko: "모든 raw 접근은 감사 기록을 남깁니다. 가장 작은 범위만 열고 보존 사유를 구체적으로 남기세요.",
      }),
      targetKind: pickAdminDashboardLocaleText(props.locale, { en: "Target kind", ko: "대상 종류" }),
      targetId: pickAdminDashboardLocaleText(props.locale, { en: "Target ID", ko: "대상 ID" }),
      purpose: pickAdminDashboardLocaleText(props.locale, { en: "Purpose", ko: "목적" }),
      reason: pickAdminDashboardLocaleText(props.locale, { en: "Reason", ko: "사유" }),
      linkedIssueId: pickAdminDashboardLocaleText(props.locale, { en: "Linked issue ID", ko: "연결 이슈 ID" }),
      submit: pickAdminDashboardLocaleText(props.locale, { en: "Request access", ko: "접근 요청" }),
      close: pickAdminDashboardLocaleText(props.locale, { en: "Close", ko: "닫기" }),
      read: pickAdminDashboardLocaleText(props.locale, { en: "Read", ko: "열람" }),
      download: pickAdminDashboardLocaleText(props.locale, { en: "Download", ko: "다운로드" }),
      validation: pickAdminDashboardLocaleText(props.locale, {
        en: "Reason must be between 10 and 500 characters.",
        ko: "사유는 10자 이상 500자 이하로 입력해야 합니다.",
      }),
    }),
    [props.locale],
  );

  if (!props.action || props.action.state === "hidden") {
    return null;
  }

  const submit = async () => {
    const normalizedReason = reason.trim();
    if (validateRecognitionRawAccessReason(normalizedReason)) {
      setValidationMessage(copy.validation);
      return;
    }

    setValidationMessage(undefined);
    await props.onSubmit({
      recognitionRunId: props.recognitionRunId,
      targetKind,
      targetId: targetId.trim(),
      purpose,
      reason: normalizedReason,
      linkedIssueId: linkedIssueId.trim() || undefined,
    });
  };

  return (
    <section style={panelStyle()}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center" }}>
        <div>
          <h4 style={{ margin: 0, fontSize: "1rem" }}>{copy.title}</h4>
          <p style={{ margin: "6px 0 0", color: "#475569", lineHeight: 1.5 }}>{copy.guidance}</p>
        </div>
        <button
          onClick={props.onClose}
          style={{ minHeight: "38px", padding: "0 12px", borderRadius: "12px", border: "1px solid rgba(148, 163, 184, 0.35)", background: "#fff" }}
          type="button"
        >
          {copy.close}
        </button>
      </div>

      <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <label style={{ display: "grid", gap: "6px" }}>
          <span>{copy.targetKind}</span>
          <select value={targetKind} onChange={(event) => setTargetKind(event.target.value as AdminRecognitionRawAccessRequest["targetKind"])} style={controlStyle()}>
            <option value="source_file">source_file</option>
            <option value="extracted_text">extracted_text</option>
            <option value="gemini_response">gemini_response</option>
          </select>
        </label>
        <label style={{ display: "grid", gap: "6px" }}>
          <span>{copy.targetId}</span>
          <input value={targetId} onChange={(event) => setTargetId(event.target.value)} style={controlStyle()} />
        </label>
        <label style={{ display: "grid", gap: "6px" }}>
          <span>{copy.purpose}</span>
          <select value={purpose} onChange={(event) => setPurpose(event.target.value as AdminRecognitionRawAccessRequest["purpose"])} style={controlStyle()}>
            <option value="failure_analysis">failure_analysis</option>
            <option value="recognition_quality_review">recognition_quality_review</option>
            <option value="security_review">security_review</option>
            <option value="user_support">user_support</option>
          </select>
        </label>
        <label style={{ display: "grid", gap: "6px" }}>
          <span>{copy.linkedIssueId}</span>
          <input value={linkedIssueId} onChange={(event) => setLinkedIssueId(event.target.value)} style={controlStyle()} />
        </label>
      </div>

      <label style={{ display: "grid", gap: "6px" }}>
        <span>{copy.reason}</span>
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} style={{ ...controlStyle(), minHeight: "108px", resize: "vertical" }} />
      </label>

      {validationMessage ? (
        <p style={{ margin: 0, color: "#b91c1c", fontWeight: 600 }}>{validationMessage}</p>
      ) : null}
      {props.error ? <p style={{ margin: 0, color: "#b91c1c", fontWeight: 600 }}>{props.error.message}</p> : null}

      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <button
          onClick={() => void submit()}
          disabled={props.submitting}
          style={{ minHeight: "40px", padding: "0 14px", borderRadius: "12px", border: "1px solid rgba(30, 64, 175, 0.22)", background: "#eff6ff", color: "#1d4ed8", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: "8px" }}
          type="button"
        >
          <ShieldAlert size={16} />
          <span>{copy.submit}</span>
        </button>
      </div>

    </section>
  );
}

export function validateRecognitionRawAccessReason(reason: string): string | undefined {
  const normalizedReason = reason.trim();
  return normalizedReason.length >= 10 && normalizedReason.length <= 500
    ? undefined
    : "Reason must be between 10 and 500 characters.";
}
